import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { AnalyticalVariableCard } from '@verantos-dev/feature-analytical-variables';
import { theme } from '@verantos-dev/shared-styles';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { AVEditPageHeader } from './AVEditPageHeader';
import { useAnalyticalVariableEditPage } from './useAnalyticalVariableEditPage';
import { CohortBuilderLoadingTemplate } from '../../components/CohortBuilderLoadingTemplate/CohortBuilderLoadingTemplate';
import { RevertCohortDefinitionModal } from '../../components/RevertCohortDefinitionModal/RevertCohortDefinitionModal';
import { SaveCohortDefinitionModal } from '../../components/SaveCohortDefinitionModal/SaveCohortDefinitionModal';
import { StagedChangesPill } from '../../components/StagedChangesPill/StagedChangesPill';
import { CohortBuilderModalProvider } from '../../contexts/CohortBuilderModalContext';

export const AnalyticalVariableEditPage = () => (
  <CohortBuilderModalProvider>
    <AnalyticalVariableEditPageContent />
  </CohortBuilderModalProvider>
);

const AnalyticalVariableEditPageContent = () => {
  const intl = useIntl();
  // OR-6123 F8: the project header no longer carries a breadcrumb, so the
  // in-page ``AVEditPageHeader`` breadcrumb (breadcrumb on top, title below)
  // shows in *both* gen_3 and legacy — each surface owns its own breadcrumb.
  // gen_3 keeps the horizontal + bottom gutter so it matches the other
  // agent-first detail pages; top padding stays omitted because
  // ``AVEditPageHeader`` contributes its own ``py: spacing(4)``.
  // TODO: cleanup with research_assistant_gen_3 ff
  const isGen3Enabled = Boolean(useFeatureFlagSplit('research_assistant_gen_3', false));
  const {
    analyticalVariable,
    conceptGroups,
    isConceptGroupsLoading,
    cohortOwner,
    cohortLastModified,
    handleAnalyticalVariableChange,
    handleDrillDownClick,
    isEditable,
    isSavedToLibrary,
    isSaveDialogOpen,
    isRevertDialogOpen,
  } = useAnalyticalVariableEditPage();

  if (!analyticalVariable || isConceptGroupsLoading) return <CohortBuilderLoadingTemplate />;

  return (
    <Box
      sx={
        isGen3Enabled
          ? {
              paddingX: theme.spacing(3),
              paddingBottom: theme.spacing(3),
            }
          : undefined
      }
    >
      <AVEditPageHeader />
      {isSaveDialogOpen && <SaveCohortDefinitionModal />}
      {isRevertDialogOpen && <RevertCohortDefinitionModal />}
      {isSavedToLibrary && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{
            mt: theme.spacing(2),
            px: 2,
            py: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: `${borderRadius.md}px`,
            mb: 4,
          }}
        >
          <InfoOutlinedIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'projects.analyticalVariables.library.editPageBanner',
            })}
          </Typography>
        </Stack>
      )}
      <Box sx={{ mt: theme.spacing(2) }}>
        <AnalyticalVariableCard
          analyticalVariable={analyticalVariable}
          conceptGroups={conceptGroups}
          owner={cohortOwner}
          lastModified={cohortLastModified}
          isEditable={isEditable}
          onAnalyticalVariableChange={handleAnalyticalVariableChange}
          onDrillDownClick={handleDrillDownClick}
        />
      </Box>
      <StagedChangesPill />
    </Box>
  );
};
